// AuthContext — single source of truth for the current user.
// Bootstraps /api/auth/me only when the non-httpOnly `fursa_session`
// marker cookie is present, so anonymous visitors skip the probe
// entirely (silences the expected 401 on every public page load).

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as authApi from "@/services/authService";

const AuthContext = createContext(null);

const hasSessionMarker = () =>
  typeof document !== "undefined" &&
  document.cookie.split(";").some((c) => c.trim().startsWith("fursa_session="));

export const AuthProvider = ({ children }) => {
  const [user, setUser]           = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    setIsLoading(true);
    if (!hasSessionMarker()) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      const u = await authApi.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    const u = await authApi.login(email, password, rememberMe);
    // Verify cookies round-tripped before setting user — protects downstream
    // /api/* consumers from firing before cookies are settled.
    let finalUser = u;
    try { const v = await authApi.me(); finalUser = v; } catch { /* keep u */ }
    setUser(finalUser);
    // Sprint 28c — immediately migrate anonymous localStorage saved list.
    try {
      const raw = window.localStorage.getItem("fursa.savedOpportunities.v1");
      const local = raw ? JSON.parse(raw) : [];
      if (Array.isArray(local) && local.length) {
        await authApi.backfillSaved(local);
      }
    } catch { /* no-op */ }
    return finalUser;
  }, []);

  const register = useCallback(async (email, password, fullName, turnstileToken = "") => {
    const u = await authApi.register(email, password, fullName, turnstileToken);
    setUser(u);
    // Also migrate anonymous localStorage saves to the freshly-created
    // account so nothing collected before signup is lost.
    try {
      const raw = window.localStorage.getItem("fursa.savedOpportunities.v1");
      const local = raw ? JSON.parse(raw) : [];
      if (Array.isArray(local) && local.length) {
        await authApi.backfillSaved(local);
      }
    } catch { /* no-op */ }
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* no-op */ }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try { const u = await authApi.me(); setUser(u); return u; }
    catch { setUser(null); return null; }
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin:  user?.role === "admin" || user?.role === "super_admin",
    isEditor: ["admin","super_admin","editor"].includes(user?.role),
    login,
    register,
    logout,
    refreshUser,
    refreshMe: bootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
